// src/components/home/BrandStory.tsx
import React, { useLayoutEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Sparkles, ArrowUpRight, BadgeCheck } from "lucide-react";
import SafeImg from "../common/SafeImg";

gsap.registerPlugin(ScrollTrigger);

/* ===== split words (reveal nicer than full line jump) ===== */
function splitWords(line: string) {
  return line.split(" ").filter(Boolean);
}

function usePrefersReducedMotion() {
  return useMemo(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
  }, []);
}

export default function BrandStory() {
  const { t } = useTranslation();
  const reducedMotion = usePrefersReducedMotion();

  const rootRef = useRef<HTMLElement | null>(null);
  const titleRef = useRef<HTMLHeadingElement | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);

  // ✅ title dạng 2 dòng để split words + animate
  const titleLines = useMemo(() => {
    const arr = t("brandStory.titleLines", { returnObjects: true }) as unknown;
    return Array.isArray(arr) ? (arr as string[]) : [String(arr || "")];
  }, [t]);

  const bullets = useMemo(() => {
    const arr = t("brandStory.bullets", { returnObjects: true }) as unknown;
    return Array.isArray(arr) ? (arr as string[]) : [];
  }, [t]);

  useLayoutEffect(() => {
    if (!rootRef.current) return;
    if (reducedMotion) return;

    const ctx = gsap.context(() => {
      const words = titleRef.current
        ? Array.from(
            titleRef.current.querySelectorAll<HTMLElement>("[data-od-word]")
          )
        : [];

      gsap.set(words, { y: 12, opacity: 0 });
      gsap.set([bodyRef.current, cardRef.current], { y: 14, opacity: 0 });

      gsap
        .timeline({
          defaults: { ease: "power3.out" },
          scrollTrigger: {
            trigger: rootRef.current,
            start: "top 78%",
            once: true,
          },
        })
        .to(words, { y: 0, opacity: 1, duration: 0.75, stagger: 0.02 })
        .to(bodyRef.current, { y: 0, opacity: 1, duration: 0.7 }, "-=0.4")
        .to(cardRef.current, { y: 0, opacity: 1, duration: 0.7 }, "-=0.5");
    }, rootRef);

    return () => ctx.revert();
  }, [reducedMotion, titleLines]);

  return (
    <section
      ref={rootRef}
      className="relative bg-brand-ivory text-brand-brown py-8 sm:py-12 lg:py-16 overflow-hidden"
    >
      {/* subtle wash (brand only) */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-brand-ivory via-brand-ivory to-brand-ivory" />
        <div className="absolute inset-0 bg-gradient-to-tr from-brand-gold/10 via-transparent to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-bl from-brand-brown/6 via-transparent to-transparent" />
      </div>

      <div className="relative mx-auto max-w-7xl px-6 sm:px-8 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* LEFT */}
          <div className="lg:col-span-6">
            {/* kicker */}
            <div className="inline-flex items-center gap-3">
              <span className="h-9 w-9 rounded-2xl border border-brand-brown/12 bg-brand-ivory/70 backdrop-blur-md grid place-items-center">
                <Sparkles className="w-4 h-4 text-brand-gold" />
              </span>
              <p className="text-[10px] uppercase tracking-[0.62em] text-brand-brown/55">
                {t("brandStory.kicker")}
              </p>
            </div>

            {/* title */}
            <h2
              ref={titleRef}
              className="mt-8 text-[30px] sm:text-[40px] lg:text-[48px]
                         leading-[1.06] uppercase tracking-[0.18em]"
            >
              {titleLines.map((line, li) => {
                const words = splitWords(line);
                return (
                  <span key={li} className="block">
                    {words.map((w, wi) => (
                      <span
                        key={`${li}-${wi}`}
                        className="inline-block overflow-hidden"
                      >
                        <span data-od-word className="inline-block">
                          {w}
                          {wi < words.length - 1 ? "\u00A0" : ""}
                        </span>
                      </span>
                    ))}
                  </span>
                );
              })}
            </h2>

            {/* body */}
            <div ref={bodyRef} className="mt-6 space-y-5 max-w-xl">
              <p className="text-sm leading-[2.0] text-brand-brown/65">
                {t("brandStory.p1")}
              </p>
              <p className="text-sm leading-[2.0] text-brand-brown/65">
                {t("brandStory.p2")}
              </p>

              {/* tiny bullets */}
              <div className="pt-2 grid gap-3">
                {bullets.map((txt) => (
                  <div key={txt} className="flex items-center gap-3">
                    <span className="h-8 w-8 rounded-2xl border border-brand-brown/10 bg-brand-ivory/70 grid place-items-center">
                      <BadgeCheck className="w-4 h-4 text-brand-gold" />
                    </span>
                    <span className="text-[10px] uppercase tracking-[0.55em] text-brand-brown/55">
                      {txt}
                    </span>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <div className="pt-4">
                <Link
                  to="/story"
                  className="inline-flex items-center justify-center gap-3 h-12 px-7 rounded-full
                             border border-brand-brown/12 bg-brand-ivory/70 backdrop-blur-md
                             text-[10px] uppercase tracking-[0.55em] text-brand-brown/70
                             hover:text-brand-brown hover:border-brand-brown/25 hover:bg-brand-brown/5 transition"
                >
                  {t("brandStory.cta")}
                  <ArrowUpRight className="w-4 h-4 text-brand-gold" />
                </Link>
              </div>
            </div>
          </div>

          {/* RIGHT (image card) */}
          <div className="lg:col-span-6">
            <div
              ref={cardRef}
              className="relative rounded-[28px] sm:rounded-[34px] overflow-hidden
                         border border-brand-brown/10 bg-brand-ivory/80 backdrop-blur-md
                         shadow-[0_40px_120px_rgba(43,31,26,0.10)]"
            >
              <div className="relative aspect-[4/5] sm:aspect-[3/4] overflow-hidden">
                {/* ✅ SafeImg để tránh loop onError + resolve đồng bộ toàn app */}
                <SafeImg
                  src="/images/brand-story.jpg"
                  alt={t("brandStory.imageAlt")}
                  className="w-full h-full object-cover"
                />

                {/* subtle overlay (brand only) */}
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-brand-brown/35 via-transparent to-transparent" />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-brand-gold/10 via-transparent to-transparent" />

                {/* micro badge */}
                <div className="absolute top-5 left-5 rounded-full px-4 py-2 border border-brand-brown/10 bg-brand-ivory/75 backdrop-blur-md">
                  <span className="text-[9px] uppercase tracking-[0.6em] text-brand-brown/55">
                    {t("brandStory.badge")}
                  </span>
                </div>
              </div>

              {/* bottom strip */}
              <div className="px-5 sm:px-6 py-4 border-t border-brand-brown/10 flex items-center justify-between gap-4">
                <p className="text-[10px] uppercase tracking-[0.55em] text-brand-brown/50">
                  {t("brandStory.strip")}
                </p>
                <span className="h-9 w-9 rounded-2xl border border-brand-brown/10 bg-brand-ivory/70 grid place-items-center">
                  <Sparkles className="w-4 h-4 text-brand-gold" />
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* divider */}
        <div className="mt-14 h-px bg-brand-brown/10" />
      </div>
    </section>
  );
}
